import { z } from "zod";

/**
 * Commands the Worker issues to an adapter (MT5 EA or Playwright driver).
 * Pushed to Redis `account:{id}:cmd` list (RPUSH), pulled by adapter via
 * GET /webhooks/commands/:accountId.
 *
 * Every command is acked via POST /webhooks/commands/:accountId/ack with
 * the `id` so the Worker can match them in `command_log`.
 */
export const OpenCmdZ = z.object({
  id: z.string().min(1), // ULID or UUID
  kind: z.literal("open"),
  symbol: z.string().max(16),
  side: z.enum(["buy", "sell"]),
  lots: z.number().positive(),
  stopLoss: z.number().nullable(),
  takeProfit: z.number().nullable(),
  comment: z.string().max(64).nullable(),
  issuedAt: z.number().int(),
});
export type OpenCmd = z.infer<typeof OpenCmdZ>;

export const CloseCmdZ = z.object({
  id: z.string().min(1),
  kind: z.literal("close"),
  ticket: z.union([z.number().int(), z.literal("all")]),
  issuedAt: z.number().int(),
});
export type CloseCmd = z.infer<typeof CloseCmdZ>;

export const ModifyCmdZ = z.object({
  id: z.string().min(1),
  kind: z.literal("modify"),
  ticket: z.number().int(),
  stopLoss: z.number().nullable(),
  takeProfit: z.number().nullable(),
  issuedAt: z.number().int(),
});
export type ModifyCmd = z.infer<typeof ModifyCmdZ>;

export const CancelCmdZ = z.object({
  id: z.string().min(1),
  kind: z.literal("cancel"),
  ticket: z.number().int(),
  issuedAt: z.number().int(),
});
export type CancelCmd = z.infer<typeof CancelCmdZ>;

export const CommandZ = z.discriminatedUnion("kind", [OpenCmdZ, CloseCmdZ, ModifyCmdZ, CancelCmdZ]);
export type Command = z.infer<typeof CommandZ>;

/** Ack payload sent back by the adapter after executing a command. */
export const CommandAckZ = z.object({
  id: z.string().min(1),
  ok: z.boolean(),
  ticket: z.number().int().nullable(),
  error: z.string().nullable(),
  executedAt: z.number().int(),
  details: z.record(z.unknown()).optional(),
});
export type CommandAck = z.infer<typeof CommandAckZ>;
